class CartDrawerController {
  constructor() {
    this.drawer = document.querySelector('[data-cart-drawer]');
    if (!this.drawer) return;

    this.panel = this.drawer.querySelector('.cart-drawer__panel');
    this.body = this.drawer.querySelector('[data-cart-drawer-body]');
    this.footer = this.drawer.querySelector('[data-cart-drawer-footer]');
    // FL-WAGEN-R2 (F-81): het express-blok staat in het scrollende vak (buiten de voet) en verdwijnt bij een lege wagen met de voet mee
    this.express = this.drawer.querySelector('[data-cart-drawer-express]');
    this.liveRegion = this.drawer.querySelector('[data-cart-drawer-live-region]');
    this.countNodes = [...document.querySelectorAll('[data-cart-count]')];
    this.toggleNodes = [...document.querySelectorAll('[data-cart-toggle]')];
    this.drawerCount = this.drawer.querySelector('[data-cart-drawer-count]');
    this.subtotalNode = this.drawer.querySelector('[data-cart-drawer-subtotal]');
    this.compareSubtotalNode = this.drawer.querySelector('[data-cart-drawer-compare-subtotal]');
    this.discountsRow = this.drawer.querySelector('[data-cart-drawer-discounts]');
    this.discountChips = this.drawer.querySelector('[data-cart-drawer-discount-chips]');
    this.discountAmount = this.drawer.querySelector('[data-cart-drawer-discount-amount]');
    this.currency = this.drawer.dataset.cartCurrency || 'USD';
    this.showCompareAt = this.drawer.dataset.cartDrawerShowCompareAt === 'true';
    this.variantCompareAtPrices = this.parseVariantCompareAtPrices();
    this.activeAddButton = null;
    this.activeAddLabel = null;
    this.activeAddLabelText = '';
    this.lastActiveElement = null;
    this.pendingItemKeys = new Set();
    this.isRefreshing = false;
    this.closeLabel = this.drawer.dataset.cartDrawerCloseLabel || '';
    this.emptyTitle = this.drawer.dataset.cartDrawerEmptyTitle || '';
    this.emptyCopy = this.drawer.dataset.cartDrawerEmptyCopy || '';
    this.continueUrl = this.drawer.dataset.cartDrawerContinueUrl || '/collections/all';
    this.continueLabel = this.drawer.dataset.cartDrawerContinueLabel || '';
    this.decreaseQuantityLabel = this.drawer.dataset.cartDrawerDecreaseLabel || '';
    this.increaseQuantityLabel = this.drawer.dataset.cartDrawerIncreaseLabel || '';
    this.quantityLabel = this.drawer.dataset.cartDrawerQuantityLabel || '';
    this.removeLabel = this.drawer.dataset.cartDrawerRemoveLabel || '';
    this.subtotalLabel = this.drawer.dataset.cartDrawerSubtotalLabel || '';
    this.secureCheckoutLabel = this.drawer.dataset.cartDrawerSecureCheckoutLabel || '';
    this.liveRegionTemplate = this.drawer.dataset.cartDrawerLiveTemplate || '';
    this.addingLabel = this.drawer.dataset.cartDrawerAddingLabel || '';
    this.loadingLabel = this.drawer.dataset.cartDrawerLoadingLabel || 'Loading cart';

    // FL-WAGEN-TEST (F-78): staffelprompt. De treden komen uit data-cart-drawer-tiers ("aantal:procent,..."); dit is alleen
    // de AANKONDIGING, de korting zelf rekent de winkel (automatische kortingen) en staat in de Discounts-rij.
    this.tierNode = this.drawer.querySelector('[data-cart-drawer-tier]');
    this.tierText = this.drawer.querySelector('[data-cart-drawer-tier-text]');
    this.tierFill = this.drawer.querySelector('[data-cart-drawer-tier-fill]');
    this.tiers = CartDrawerController.parseTiers(this.drawer.dataset.cartDrawerTiers);
    this.tierMoreTemplate = this.drawer.dataset.cartDrawerTierMore || '';
    this.tierMaxTemplate = this.drawer.dataset.cartDrawerTierMax || '';

    this.bindUI();
  }

  // Treden uit "2:10,3:15,4:20,5:25": ongeldige, dubbele of onzinnige delen (aantal < 2, procent buiten 1-90) vallen weg; oplopend op aantal.
  static parseTiers(text) {
    const tiers = [];
    String(text == null ? '' : text).split(',').forEach((part) => {
      const match = /^\s*(\d{1,3})\s*:\s*(\d{1,2})\s*$/.exec(part);
      if (!match) return;
      const count = parseInt(match[1], 10);
      const pct = parseInt(match[2], 10);
      if (count < 2 || pct < 1 || pct > 90) return;
      if (tiers.some((tier) => tier.count === count)) return;
      tiers.push({ count, pct });
    });
    return tiers.sort((a, b) => a.count - b.count);
  }

  // Stand bij `quantity` artikelen: hoogste bereikte trede, volgende trede, hoeveel er nog bij moet en de vulling (0-100,
  // van 1 artikel tot de hoogste trede). Zonder treden of bij quantity < 1: null.
  static tierState(quantity, tiers) {
    if (!Number.isInteger(quantity) || quantity < 1 || !tiers || !tiers.length) return null;
    let current = null;
    let next = null;
    tiers.forEach((tier) => {
      if (quantity >= tier.count) current = tier;
      else if (!next) next = tier;
    });
    const top = tiers[tiers.length - 1].count;
    const fill = top <= 1 ? 100 : Math.max(0, Math.min(100, Math.round(((Math.min(quantity, top) - 1) / (top - 1)) * 100)));
    return { current, next, missing: next ? next.count - quantity : 0, fill };
  }

  renderTier(itemCount) {
    if (!this.tierNode || !this.tierText || !this.tierFill) return;

    const state = CartDrawerController.tierState(itemCount, this.tiers);
    if (!state || !this.tierMoreTemplate || !this.tierMaxTemplate) {
      this.tierNode.hidden = true;
      return;
    }

    this.tierText.textContent = state.next
      ? this.tierMoreTemplate.replace('{n}', String(state.missing)).replace('{pct}', String(state.next.pct))
      : this.tierMaxTemplate.replace('{pct}', String(state.current.pct));
    this.tierFill.style.width = `${state.fill}%`;
    this.tierNode.hidden = false;
  }

  bindUI() {
    this.toggleNodes.forEach((trigger) => {
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.addEventListener('click', async (event) => {
        event.preventDefault();
        this.lastActiveElement = trigger;
        await this.refresh();
        this.open();
      });
    });

    this.drawer.querySelectorAll('[data-cart-drawer-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.drawer.classList.contains('is-open')) {
        this.close();
      } else if (event.key === 'Tab' && this.drawer.classList.contains('is-open')) {
        this.trapFocus(event);
      }
    });

    document.addEventListener('submit', (event) => this.handleAddToCart(event));
    this.drawer.addEventListener('click', (event) => this.handleDrawerClick(event));
    this.drawer.addEventListener('change', (event) => this.handleDrawerChange(event));
  }

  async handleAddToCart(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.matches('[data-product-form]')) return;

    const submitter = event.submitter;
    if (!submitter || submitter.getAttribute('name') !== 'add') return;

    event.preventDefault();

    const formData = new FormData(form);
    this.activeAddButton = submitter;
    this.activeAddLabel = submitter.querySelector('[data-add-to-cart-label]');
    this.activeAddLabelText = this.activeAddLabel?.textContent || '';
    this.cacheFormVariantCompareAt(form);
    this.hideAddToCartError(form);

    try {
      this.setAddButtonState(true);

      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json'
        },
        body: formData
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.description || data?.message || '');
      }

      await response.json();
      await this.refresh();
      this.open();
    } catch (error) {
      console.error(error);
      this.showAddToCartError(form, error?.message);
    } finally {
      this.setAddButtonState(false);
    }
  }

  showAddToCartError(form, message) {
    const errorElement = form.querySelector('[data-add-to-cart-error]');
    if (!errorElement) {
      window.location.href = form.action || '/cart';
      return;
    }

    errorElement.textContent = message || errorElement.dataset.genericError || '';
    errorElement.hidden = false;
  }

  hideAddToCartError(form) {
    const errorElement = form.querySelector('[data-add-to-cart-error]');
    if (errorElement) {
      errorElement.hidden = true;
      errorElement.textContent = '';
    }
  }

  async handleDrawerClick(event) {
    const quantityTrigger = event.target.closest('[data-cart-quantity-change]');
    if (quantityTrigger) {
      const item = quantityTrigger.closest('[data-cart-item-key]');
      if (!item) return;

      const key = item.dataset.cartItemKey;
      if (!key || this.pendingItemKeys.has(key)) return;

      const delta = Number(quantityTrigger.dataset.cartQuantityDelta || 0);
      const quantityValue = item.querySelector('.cart-drawer__quantity-value');
      const currentQuantity = Number(quantityValue?.value ?? quantityValue?.textContent ?? 0);
      const nextQuantity = Math.max(0, currentQuantity + delta);

      await this.changeItem(key, nextQuantity);
      return;
    }

    const removeTrigger = event.target.closest('[data-cart-remove]');
    if (removeTrigger) {
      const item = removeTrigger.closest('[data-cart-item-key]');
      if (!item) return;

      const key = item.dataset.cartItemKey;
      if (!key || this.pendingItemKeys.has(key)) return;

      await this.changeItem(key, 0);
    }
  }

  async handleDrawerChange(event) {
    const input = event.target.closest('[data-cart-quantity-input]');
    if (!input) return;

    const item = input.closest('[data-cart-item-key]');
    if (!item) return;

    const key = item.dataset.cartItemKey;
    if (!key || this.pendingItemKeys.has(key)) return;

    const quantity = Math.max(0, Number(input.value) || 0);
    await this.changeItem(key, quantity);
  }

  async changeItem(key, quantity) {
    if (!key || this.pendingItemKeys.has(key)) return;

    this.pendingItemKeys.add(key);
    this.setItemPendingState(key, true);

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: key,
          quantity
        })
      });

      if (!response.ok) {
        throw new Error('Unable to update cart item');
      }

      const cart = await response.json();
      this.render(cart);
    } catch (error) {
      console.error(error);
      this.pendingItemKeys.delete(key);
      await this.refresh();
    } finally {
      this.pendingItemKeys.delete(key);
      this.setItemPendingState(key, false);
    }
  }

  showSkeleton(count = 2) {
    if (!this.body) return;

    const items = Array.from({ length: count }, () => `
      <div class="cart-drawer__skeleton-item" aria-hidden="true">
        <div class="cart-drawer__skeleton-media"></div>
        <div class="cart-drawer__skeleton-lines">
          <div class="cart-drawer__skeleton-line"></div>
          <div class="cart-drawer__skeleton-line"></div>
          <div class="cart-drawer__skeleton-line"></div>
        </div>
      </div>
    `).join('');

    this.body.innerHTML = `<div class="cart-drawer__skeleton" aria-busy="true" aria-label="${this.escapeHtml(this.loadingLabel)}">${items}</div>`;
  }

  async refresh() {
    if (this.isRefreshing) return null;

    this.isRefreshing = true;

    const currentCount = this.body ? this.body.querySelectorAll('[data-cart-item-key]').length : 0;
    if (currentCount > 0 || (this.body && !this.body.children.length)) {
      this.showSkeleton(Math.max(1, Math.min(currentCount, 3)));
    }

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: {
          Accept: 'application/json'
        }
      });
      const cart = await response.json();
      this.render(cart);
      return cart;
    } finally {
      this.isRefreshing = false;
    }
  }

  discountSummary(cart) {
    const titles = [];
    const add = (title) => {
      const t = String(title || '').trim();
      if (t && !titles.includes(t)) titles.push(t);
    };
    (cart.cart_level_discount_applications || []).forEach((application) => add(application.title));
    (cart.items || []).forEach((item) => {
      (item.line_level_discount_allocations || []).forEach((allocation) => add(allocation.discount_application?.title));
    });
    return { amount: Number(cart.total_discount || 0), titles };
  }

  optionsLine(item) {
    return (item.options_with_values || [])
      .filter((option) => option && option.value && option.value !== 'Default Title' && option.name !== 'Title')
      .map((option) => `${option.name}: ${option.value}`)
      .join(', ');
  }

  buildItemMarkup(item) {
    const imageMarkup = item.image
      ? `<a href="${this.escapeHtml(item.url)}" class="cart-drawer__item-media">
          <img src="${this.escapeHtml(item.image)}" alt="${this.escapeHtml(item.product_title)}" class="cart-drawer__item-image" loading="lazy">
        </a>`
      : `<div class="cart-drawer__item-media"></div>`;

    const optionsText = this.optionsLine(item);
    const variantMarkup = optionsText ? `<p class="cart-drawer__item-variant">${this.escapeHtml(optionsText)}</p>` : '';

    // De regelprijs is de prijs VOOR de staffelkorting; de korting staat apart in de voet.
    const linePrice = item.original_line_price ?? item.final_line_price ?? 0;
    const comparePriceMarkup =
      this.showCompareAt && (item.compare_at_line_price || 0) > linePrice
        ? `<p class="cart-drawer__item-compare-price">${this.formatMoney(item.compare_at_line_price)}</p>`
        : '';

    const isPending = this.pendingItemKeys.has(item.key);
    const dis = isPending ? 'disabled' : '';
    const title = this.escapeHtml(item.product_title);

    return `
      <li class="cart-drawer__item${isPending ? ' is-pending' : ''}" data-cart-item-key="${this.escapeHtml(item.key)}" aria-busy="${isPending ? 'true' : 'false'}">
        ${imageMarkup}
        <div class="cart-drawer__item-content">
          <div class="cart-drawer__item-top">
            <a href="${this.escapeHtml(item.url)}" class="cart-drawer__item-title">${title}</a>
            <button type="button" class="cart-drawer__remove" data-cart-remove ${dis} aria-label="${this.escapeHtml(this.removeLabel)}: ${title}">${CartDrawerController.TRASH_ICON}</button>
          </div>
          ${variantMarkup}
          <div class="cart-drawer__item-prices">
            ${comparePriceMarkup}
            <p class="cart-drawer__item-price">${this.formatMoney(linePrice)}</p>
          </div>
          <div class="cart-drawer__quantity">
            <button type="button" class="cart-drawer__quantity-button" data-cart-quantity-change data-cart-quantity-delta="-1" aria-label="${this.escapeHtml(this.decreaseQuantityLabel)}" ${dis}>-</button>
            <input type="number" class="cart-drawer__quantity-value" value="${Number(item.quantity) || 0}" min="1" aria-label="${this.escapeHtml(this.quantityLabel)}" data-cart-quantity-input ${dis}>
            <button type="button" class="cart-drawer__quantity-button" data-cart-quantity-change data-cart-quantity-delta="1" aria-label="${this.escapeHtml(this.increaseQuantityLabel)}" ${dis}>+</button>
          </div>
        </div>
      </li>
    `;
  }

  buildDiscountChips(titles) {
    return titles
      .map((title) => `<span class="cart-drawer__discount-chip">${CartDrawerController.TAG_ICON}${this.escapeHtml(title)}</span>`)
      .join('');
  }

  render(cart) {
    this.hydrateCompareAtPrices(cart);
    const itemCount = cart.item_count || 0;

    this.countNodes.forEach((node) => {
      node.textContent = itemCount;
      node.hidden = itemCount === 0;
    });

    if (this.subtotalNode) {
      this.subtotalNode.textContent = this.formatMoney(cart.total_price || 0);
    }

    if (this.liveRegion) {
      this.liveRegion.textContent = this.liveRegionTemplate
        .replace('[[count]]', String(itemCount))
        .replace('[[subtotal]]', this.formatMoney(cart.total_price || 0));
    }

    if (this.discountsRow) {
      const summary = this.discountSummary(cart);
      const show = summary.amount > 0;
      this.discountsRow.hidden = !show;
      if (this.discountChips) this.discountChips.innerHTML = show ? this.buildDiscountChips(summary.titles) : '';
      if (this.discountAmount) this.discountAmount.textContent = show ? `- ${this.formatMoney(summary.amount)}` : '';
    }

    if (this.footer) {
      this.footer.hidden = itemCount === 0;
    }

    if (this.express) {
      this.express.hidden = itemCount === 0;
    }

    this.renderTier(itemCount);

    if (!itemCount) {
      this.body.innerHTML = `
        <div class="cart-drawer__empty" data-cart-drawer-empty>
          <p class="cart-drawer__empty-title">${this.escapeHtml(this.emptyTitle)}</p>
          <p class="cart-drawer__empty-copy">${this.escapeHtml(this.emptyCopy)}</p>
          <a href="${this.escapeHtml(this.continueUrl)}" class="button button--primary cart-drawer__empty-cta">
            ${this.escapeHtml(this.continueLabel)}
          </a>
        </div>
      `;
      return;
    }

    this.body.innerHTML = `<ul class="cart-drawer__items" data-cart-drawer-items role="list">${cart.items.map((item) => this.buildItemMarkup(item)).join('')}</ul>`;
  }

  setBackgroundInert(isInert) {
    if (isInert) {
      this.inertedElements = [...document.body.children].filter(
        (el) => el !== this.drawer && el.tagName !== 'SCRIPT' && !el.hasAttribute('inert')
      );
      this.inertedElements.forEach((el) => el.setAttribute('inert', ''));
    } else {
      (this.inertedElements || []).forEach((el) => el.removeAttribute('inert'));
      this.inertedElements = [];
    }
  }

  open() {
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-drawer-open');
    this.toggleNodes.forEach((node) => node.setAttribute('aria-expanded', 'true'));
    this.setBackgroundInert(true);

    const focusTarget =
      this.panel.querySelector('#CartDrawerTitle') ||
      this.panel.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    focusTarget?.focus();
  }

  close() {
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-drawer-open');
    this.toggleNodes.forEach((node) => node.setAttribute('aria-expanded', 'false'));
    this.setBackgroundInert(false);
    this.lastActiveElement?.focus();
  }

  setAddButtonState(isLoading) {
    if (!this.activeAddButton) return;

    this.activeAddButton.disabled = isLoading;
    if (this.activeAddLabel) {
      this.activeAddLabel.textContent = isLoading ? this.addingLabel : this.activeAddLabelText;
    }
  }

  setItemPendingState(key, isPending) {
    const item = this.body?.querySelector(`[data-cart-item-key="${CSS.escape(key)}"]`);
    if (!item) return;

    item.classList.toggle('is-pending', isPending);
    item.setAttribute('aria-busy', isPending ? 'true' : 'false');

    item.querySelectorAll('button').forEach((button) => {
      button.disabled = isPending;
    });
  }

  trapFocus(event) {
    const focusableNodes = [...this.panel.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')]
      .filter((node) => !node.hasAttribute('disabled'));
    if (!focusableNodes.length) return;

    const firstNode = focusableNodes[0];
    const lastNode = focusableNodes[focusableNodes.length - 1];

    if (event.shiftKey && document.activeElement === firstNode) {
      event.preventDefault();
      lastNode.focus();
    } else if (!event.shiftKey && document.activeElement === lastNode) {
      event.preventDefault();
      firstNode.focus();
    }
  }

  formatMoney(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency: this.currency
    }).format((cents || 0) / 100);
  }

  escapeHtml(value) {
    return String(value || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  parseJsonNode(selector) {
    const node = this.drawer.querySelector(selector);
    if (!node) return null;

    try {
      return JSON.parse(node.textContent || '{}');
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  parseVariantCompareAtPrices() {
    const value = this.parseJsonNode('[data-cart-drawer-compare-at-prices]');
    const prices = new Map();

    Object.entries(value || {}).forEach(([variantId, compareAtPrice]) => {
      const normalizedId = Number(variantId);
      if (!normalizedId) return;

      prices.set(normalizedId, Number(compareAtPrice || 0));
    });

    return prices;
  }

  cacheFormVariantCompareAt(form) {
    if (!this.showCompareAt) return;

    const variantId = Number(form.querySelector('[name="id"]')?.value || 0);
    if (!variantId || this.variantCompareAtPrices.has(variantId)) return;

    const productJsonNode = form.closest('[data-product-section]')?.querySelector('[data-product-json]');
    if (!productJsonNode) return;

    try {
      const product = JSON.parse(productJsonNode.textContent || '{}');
      const variant = (product.variants || []).find((item) => Number(item.id) === variantId);
      if (!variant) return;

      this.variantCompareAtPrices.set(variantId, Number(variant.compare_at_price || 0));
    } catch (error) {
      console.error(error);
    }
  }

  hydrateCompareAtPrices(cart) {
    if (!this.showCompareAt || !cart?.items?.length) return;

    cart.items.forEach((item) => {
      const variantId = Number(item.variant_id || 0);
      const compareAtUnitPrice = Number(this.variantCompareAtPrices.get(variantId) || 0);
      const finalUnitPrice = Number(item.original_price ?? item.final_price ?? 0);

      item.compare_at_unit_price = compareAtUnitPrice;
      item.compare_at_line_price =
        compareAtUnitPrice > finalUnitPrice ? compareAtUnitPrice * Number(item.quantity || 0) : 0;
    });
  }
}

CartDrawerController.TRASH_ICON = '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7h16M10 11v6M14 11v6M6 7l1 12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-12M9 7V4h6v3"></path></svg>';
CartDrawerController.TAG_ICON = '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="currentColor"><path d="M3 12V4a1 1 0 0 1 1-1h8l9 9-9 9-9-9zm5-6a2 2 0 1 0 0 4 2 2 0 0 0 0-4z"></path></svg>';

document.addEventListener('DOMContentLoaded', () => {
  new CartDrawerController();
});
